<template>
    <div class="health-report-page">
        <van-list
            v-model="loading"
            :finished="finished"
            finished-text="没有更多了"
            @load="onLoad"
        >
            <div v-if="reports.length" class="list-wrap">
                <div class="report-card" v-for="item in reports" :key="item.rowKey">
                    <div class="main-row">
                        <div class="left-user">
                            <van-icon name="shield-o" color="#46ccdc" size="18" />
                            <span class="name">{{ item.name }}</span>
                            <span v-if="item.pending" class="pending-tag">待体检</span>
                        </div>
                        <van-button
                            v-if="item.reportUrl"
                            type="info"
                            size="small"
                            round
                            @click="openReport(item.reportUrl)"
                            >查看报告</van-button
                        >
                        <van-button
                            v-else
                            type="info"
                            size="small"
                            round
                            plain
                            @click="$toast('暂无报告')"
                            >查看报告</van-button
                        >
                    </div>
                    <div class="info-row">
                        <span class="label">体检时间</span>
                        <span class="value">{{ item.examinationDateText }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">体检类型</span>
                        <span class="value">{{ item.examType }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">体检结果</span>
                        <span class="value">{{ item.resultContent }}</span>
                    </div>
                    <div v-if="item.createTimeText !== '—'" class="info-row subtle">
                        <span class="label">报告上传</span>
                        <span class="value">{{ item.createTimeText }}</span>
                    </div>
                    <div v-if="item.expirationTimeText !== '—'" class="info-row subtle">
                        <span class="label">到期时间</span>
                        <span class="value">{{ item.expirationTimeText }}</span>
                    </div>
                    <div v-if="item.expiredStatusText" class="info-row subtle">
                        <span class="label">是否到期</span>
                        <span class="value">{{ item.expiredStatusText }}</span>
                    </div>
                    <div v-if="item.mName" class="info-row subtle">
                        <span class="label">医疗机构</span>
                        <span class="value">{{ item.mName }}</span>
                    </div>
                    <div v-if="item.work" class="info-row subtle">
                        <span class="label">从事工作</span>
                        <span class="value">{{ item.work }}</span>
                    </div>
                    <div v-if="item.workingState" class="info-row subtle">
                        <span class="label">工作状态</span>
                        <span class="value">{{ item.workingState }}</span>
                    </div>
                </div>
            </div>
        </van-list>

        <div v-if="!loading && finished && reports.length === 0" class="empty-wrap">
            <van-empty description="暂无健康报告" />
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { getHealthReports } from '@/api/index'

export default {
    name: 'HealthReport',
    data() {
        return {
            reports: [],
            pageNum: 1,
            pageSize: 10,
            loading: false,
            finished: false,
            total: 0
        }
    },
    created() {
        this.fetchList(false)
    },
    methods: {
        onLoad() {
            this.fetchList(true)
        },
        fetchList(isLoadMore = false) {
            if (this.loading || this.finished) return

            this.loading = true
            getHealthReports({
                pageNum: this.pageNum,
                pageSize: this.pageSize
            })
                .then(res => {
                    const rawList =
                        res.data?.records ||
                        res.data?.list ||
                        res.data?.rows ||
                        (Array.isArray(res.data) ? res.data : [])
                    const records = rawList.map((row, i) => this.normalizeRow(row, i))
                    const total = Number(res.data?.total ?? res.data?.totalCount ?? 0)

                    if (isLoadMore) {
                        this.reports = [...this.reports, ...records]
                    } else {
                        this.reports = records
                    }

                    this.total = total
                    if (this.reports.length >= total || records.length < this.pageSize) {
                        this.finished = true
                    } else {
                        this.pageNum += 1
                    }
                    this.loading = false
                })
                .catch(() => {
                    this.loading = false
                    this.finished = true
                })
        },
        formatDateTime(val) {
            if (val === undefined || val === null || val === '') return '—'
            const m = moment(val)
            return m.isValid() ? m.format('YYYY年MM月DD日 HH:mm') : String(val)
        },
        formatDateOnly(val) {
            if (val === undefined || val === null || val === '') return '—'
            const m = moment(val)
            return m.isValid() ? m.format('YYYY年MM月DD日') : String(val)
        },
        /** state：是否待检测，0 否、1 是；可能为布尔 */
        isPending(state) {
            if (state === true || state === 1 || state === '1') return true
            if (state === false || state === 0 || state === '0') return false
            return false
        },
        normalizeRow(raw, index) {
            const id = raw.id
            const rowKey =
                id !== undefined && id !== null
                    ? `health-${id}-${index}`
                    : `health-${index}-${raw.medicalExaminationDate || raw.createTime || index}`

            const reportRaw = (raw.report || '').trim()
            const reportUrl = /^https?:\/\//i.test(reportRaw) ? reportRaw : ''

            return {
                rowKey,
                name: (raw.name || '—').trim() || '—',
                pending: this.isPending(raw.state),
                examinationDateText: this.formatDateOnly(raw.medicalExaminationDate),
                examType: (raw.type && String(raw.type).trim()) || '—',
                resultContent: (raw.resultContent && String(raw.resultContent).trim()) || '—',
                createTimeText: this.formatDateTime(raw.createTime),
                expirationTimeText: this.formatDateOnly(raw.expirationTime),
                expiredStatusText: (raw.expiredStatus && String(raw.expiredStatus).trim()) || '',
                mName: (raw.mName || '').trim(),
                work: (raw.work || '').trim(),
                workingState: (raw.workingState || '').trim(),
                reportUrl
            }
        },
        openReport(url) {
            if (!url) {
                this.$toast('暂无报告链接')
                return
            }
            window.location.href = url
        }
    }
};
</script>

<style scoped lang="scss">
.health-report-page {
    min-height: 100vh;
    background: #eef7f8;
    padding: 12px;
    box-sizing: border-box;
}

.list-wrap {
    padding-bottom: 8px;
}

.empty-wrap {
    padding: 48px 12px;
}

.report-card {
    background: #fff;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
}

.main-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.left-user {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    min-width: 0;
}

.name {
    color: #262626;
    font-size: 16px;
    font-weight: 600;
}

.pending-tag {
    font-size: 11px;
    color: #ff8a4c;
    background: rgba(255, 138, 76, 0.12);
    padding: 2px 6px;
    border-radius: 4px;
}

.info-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-top: 6px;

    &.subtle {
        .label,
        .value {
            font-size: 13px;
        }
    }
}

.label {
    flex-shrink: 0;
    width: 72px;
    color: #b3b3b3;
    font-size: 13px;
}

.value {
    flex: 1;
    color: #333;
    font-size: 15px;
    line-height: 1.45;
    word-break: break-all;
}

::v-deep .van-button {
    background: #46ccdc;
    border-color: #46ccdc;
    height: 28px;
    padding: 0 14px;
    font-size: 12px;
    flex-shrink: 0;
}

::v-deep .van-button--plain {
    background: transparent;
    color: #46ccdc;
}
</style>
